import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";

import {
  LuBold,
  LuItalic,
  LuUnderline,
  LuAlignLeft,
  LuAlignCenter,
  LuAlignRight,
  LuAlignJustify,
  LuHighlighter,
  LuUndo2,
  LuRedo2,
} from "react-icons/lu";

import "./FooterDescriptionEditor.css";
import StyledSelect from "../StyledSelect";

import sanitizeRichHtml from
  "../../utils/sanitizeRichHtml";

function FooterDescriptionEditor({
  value = "",
  onChange
}) {

  const editor = useEditor({

    extensions: [

      StarterKit,

      Underline,

      Highlight.configure({
        multicolor: true
      }),

      TextAlign.configure({
        types: [
          "heading",
          "paragraph"
        ]
      }),

    ],

    content:
      sanitizeRichHtml(
        value || ""
      ),

    onUpdate: ({ editor }) => {

      onChange(
        editor.getHTML()
      );

    },

  });


  useEffect(() => {

    if (
      editor &&
      value !== editor.getHTML()
    ) {

      editor.commands.setContent(
        sanitizeRichHtml(
          value || ""
        )
      );

    }

  }, [value, editor]);


  if (!editor) {
    return null;
  }


  return (

    <div className="footer-description-editor">

      <div className="footer-description-toolbar">


        {/* BOLD */}

        <button
          type="button"
          title="Bold"
          className={
            editor.isActive("bold")
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          }
        >
          <LuBold />
        </button>


        {/* ITALIC */}

        <button
          type="button"
          title="Italic"
          className={
            editor.isActive("italic")
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          }
        >
          <LuItalic />
        </button>


        {/* UNDERLINE */}

        <button
          type="button"
          title="Underline"
          className={
            editor.isActive("underline")
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleUnderline()
              .run()
          }
        >
          <LuUnderline />
        </button>


        <span className="footer-description-toolbar-separator" />


        {/* LEFT */}

        <button
          type="button"
          title="Align Left"
          className={
            editor.isActive({
              textAlign: "left"
            })
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("left")
              .run()
          }
        >
          <LuAlignLeft />
        </button>


        {/* CENTER */}

        <button
          type="button"
          title="Align Center"
          className={
            editor.isActive({
              textAlign: "center"
            })
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("center")
              .run()
          }
        >
          <LuAlignCenter />
        </button>


        {/* RIGHT */}

        <button
          type="button"
          title="Align Right"
          className={
            editor.isActive({
              textAlign: "right"
            })
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("right")
              .run()
          }
        >
          <LuAlignRight />
        </button>


        {/* JUSTIFY */}

        <button
          type="button"
          title="Justify"
          className={
            editor.isActive({
              textAlign: "justify"
            })
              ? "active"
              : ""
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("justify")
              .run()
          }
        >
          <LuAlignJustify />
        </button>


        <span className="footer-description-toolbar-separator" />


        {/* HIGHLIGHT */}

        <div
          className="footer-description-highlight-control"
        >

          <LuHighlighter
            title="Highlight Colour"
            style={{
              fontSize: "18px"
            }}
          />

          <StyledSelect
            value=""
            placeholder="Highlight"
            ariaLabel="Highlight colour"
            title="Highlight Colour"
            className="styled-select--toolbar styled-select--toolbar-highlight"
            options={[
              { value: "#fff59d", label: "Yellow" },
              { value: "#ff6767", label: "Red" },
              { value: "#c8e6c9", label: "Green" },
              { value: "#bbdefb", label: "Blue" },
              { value: "#f8bbd0", label: "Pink" },
              { value: "#ffe0b2", label: "Orange" },
              { value: "#e1bee7", label: "Purple" },
              { value: "#e0e0e0", label: "Grey" },
              { value: "remove", label: "Remove Highlight" }
            ]}
            onChange={(e) => {

              const color =
                e.target.value;

              if (color === "remove") {

                editor
                  .chain()
                  .focus()
                  .unsetHighlight()
                  .run();

              } else if (color) {

                editor
                  .chain()
                  .focus()
                  .setHighlight({
                    color
                  })
                  .run();

              }

            }}
          />

        </div>


        <span className="footer-description-toolbar-separator" />


        {/* UNDO */}

        <button
          type="button"
          title="Undo"
          onClick={() =>
            editor
              .chain()
              .focus()
              .undo()
              .run()
          }
        >
          <LuUndo2 />
        </button>


        {/* REDO */}

        <button
          type="button"
          title="Redo"
          onClick={() =>
            editor
              .chain()
              .focus()
              .redo()
              .run()
          }
        >
          <LuRedo2 />
        </button>

      </div>


      <EditorContent
        editor={editor}
      />

    </div>

  );

}

export default FooterDescriptionEditor;
